import type { TeactNode } from '../../../lib/teact/teact';
import React from '../../../lib/teact/teact';
import { getActions, withGlobal } from '../../../global';

import type { ApiPeer, ApiUser } from '../../../api/types';
import { ApiMessageEntityTypes } from '../../../api/types';

import { getUserFullName, isUserId } from '../../../global/helpers';
import { selectChat, selectUser } from '../../../global/selectors';
import { selectIsCurrentUserPrivileged } from '../../../global/selectors/users';

import useAppLayout from '../../../hooks/useAppLayout';

type OwnProps = {
  userId?: string;
  username?: string;
  chatId?: string;
  children: TeactNode;
};

type StateProps = {
  userOrChat?: ApiPeer;
  displayName?: string;
  isCurrentUserPrivileged?: boolean;
  chat?: { defaultBannedRights?: { addContact?: true }; isCreator?: boolean };
};

const MentionLink = ({
  userId,
  username,
  chatId,
  userOrChat,
  displayName,
  isCurrentUserPrivileged,
  chat,
  children,
}: OwnProps & StateProps) => {
  const {
    openChat,
    openChatByUsername,
    closeStoryViewer,
    setShouldCloseRightColumn,
    openLeftColumnUserProfile,
    showNotification,
  } = getActions();

  const { isMobile } = useAppLayout();

  const handleClick = () => {
    if (isMobile) {
      setShouldCloseRightColumn({
        value: true,
      });
    }

    // SSGram: 群聊中点击 @mention 与头像/发件人点击逻辑一致：非特权号打开个人信息页
    const targetUserId = userOrChat?.id;
    if (chatId && targetUserId && isUserId(targetUserId) && !isUserId(chatId)) {
      if (chat?.defaultBannedRights?.addContact && !chat?.isCreator) {
        showNotification({ message: '当前群组不允许查看成员资料' });
        return;
      }
      if (!isCurrentUserPrivileged) {
        openLeftColumnUserProfile({ userId: targetUserId });
        return;
      }
    }

    if (userOrChat) {
      openChat({ id: userOrChat.id });
    } else if (username) {
      closeStoryViewer();
      openChatByUsername({ username: username.startsWith('@') ? username.substring(1) : username });
    }
  };

  return (
    <a
      onClick={handleClick}
      className="text-entity-link"
      dir="auto"
      data-entity-type={userId ? ApiMessageEntityTypes.MentionName : ApiMessageEntityTypes.Mention}
    >
      {displayName ? `@${displayName}` : children}
    </a>
  );
};

export default withGlobal<OwnProps>(
  (global, { userId, username, chatId }): StateProps => {
    const chat = chatId ? selectChat(global, chatId) : undefined;
    const isCurrentUserPrivileged = selectIsCurrentUserPrivileged(global);
    const base = { chat, isCurrentUserPrivileged };

    if (userId) {
      return {
        ...base,
        userOrChat: selectUser(global, userId),
      };
    }

    // SSGram: 通过 username 查找用户，显示昵称而非 username
    if (username) {
      const cleanUsername = username.startsWith('@') ? username.substring(1).toLowerCase() : username.toLowerCase();
      const matchedUser = Object.values(global.users.byId).find(
        (u: ApiUser) => u.usernames?.some((un) => un.username.toLowerCase() === cleanUsername),
      );
      if (matchedUser) {
        const name = getUserFullName(matchedUser);
        return {
          ...base,
          userOrChat: matchedUser,
          displayName: name || undefined,
        };
      }
    }

    return { ...base };
  },
)(MentionLink);
